import { type FC, useCallback } from 'react'

import { DialingFooter } from '@liveHelper/components/shared/Dialing/DialingFooter'
import { DialingHeader } from '@liveHelper/components/shared/Dialing/DialingHeader'
import { useDraggableContext } from '@liveHelper/context/DraggableContext'
import { useTopLevelUIState } from '@liveHelper/hooks/useTopLevelUIState'
import {
  actionsSelector,
  uiStateSelector,
  useStore,
} from '@liveHelper/models/store'
import { QUEUE_POSITION_CONTAINER } from '@liveHelper/testIds'

import { useContextTranslation } from '@core/i18n'
import { isMobileWeb } from '@core/userAgent'

import {
  QueuePositionContainer,
  QueuePositionNumber,
  QueueText,
} from './QueuePosition.styles'

interface QueuePositionProps {
  queuePosition: number
}

/** Live helper widget queue component shared between "idle_minimized and "dialing_minimized" state. */
export const QueuePosition: FC<QueuePositionProps> = ({ queuePosition }) => {
  const { t } = useContextTranslation()

  const uiState = useStore(uiStateSelector)
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)
  const topLevelUIState = useTopLevelUIState()

  const isExpanded = uiState === 'dialing_expanded'

  const onClick = useCallback(() => {
    if (uiState === 'dialing_minimized') {
      sendUIStateTransitionEvent('EXPAND')
    } else if (uiState === 'idle_minimized') {
      sendUIStateTransitionEvent('START_CHAT')
    }
  }, [sendUIStateTransitionEvent, uiState])

  const draggableContext = useDraggableContext()
  const listeners = draggableContext ? draggableContext.listeners : {}

  return (
    <QueuePositionContainer
      tabIndex={0}
      aria-label={t('oneToOne:One-to-one chat')}
      topLevelUIState={topLevelUIState}
      data-testid={QUEUE_POSITION_CONTAINER}
      onClick={onClick}
      onKeyUp={(e) => {
        if (e.key === 'Enter') {
          onClick()
        }
      }}
      {...listeners}
      isMobileWeb={isMobileWeb()}
    >
      <DialingHeader />
      <QueueText large={isExpanded}>
        {t('oneToOne:You are currently')}
      </QueueText>
      <QueuePositionNumber>{queuePosition}</QueuePositionNumber>
      <QueueText large={isExpanded}>{t('oneToOne:in queue')}</QueueText>
      <DialingFooter />
    </QueuePositionContainer>
  )
}
